/**
 * 路由编辑弹窗：一个对外模型的降级链（渠道 + 上游模型 + 优先级 + 同层权重 + 节点参数）。
 *
 * 背景：从旧的 Models 页拆出，保留全部能力；Alice 版式的路由工作台（行内展开）负责日常调整，这里保留完整的整组提交。
 * 设计意图：路由按模型整组提交；上游模型只能从渠道模型库选（后端同样校验）；节点参数（request_overrides）优先级最高。
 * 关键约束：只有 admin 能保存；没有启用路由的模型调用会失败。
 */
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { catalogApi, type CatalogChannel, type CatalogModel } from '@ap/api-client/admin-catalog'
import type { ChannelModel, Route } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'
import {
  Button,
  Card,
  Field,
  Form,
  Input,
  Modal,
  Notice,
  Select,
  Switch,
  Textarea,
  useToast,
} from '@ap/ui'
import { isEmptyObj, parseJson, showJson } from './shared.ts'

interface RouteRow {
  channel_id: string
  upstream_model: string
  priority: string
  weight: string
  overrides: string
  enabled: boolean
}

const toRow = (r: Route): RouteRow => ({
  channel_id: r.channel_id,
  upstream_model: r.upstream_model,
  priority: String(r.priority),
  weight: String(r.weight),
  overrides: showJson(r.request_overrides, isEmptyObj(r.request_overrides)),
  enabled: r.enabled,
})

export function RoutesEditor({
  model,
  channels,
  library,
  onClose,
  onSaved,
}: {
  model: CatalogModel | null
  channels: CatalogChannel[]
  library: ChannelModel[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [rows, setRows] = useState<RouteRow[]>([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [shown, setShown] = useState<CatalogModel | null>(null)
  if (model !== shown) {
    setShown(model)
    if (model) {
      setRows(model.routes.map(toRow))
      setReason('')
      setError('')
    }
  }
  const patch = (i: number, k: keyof RouteRow, v: string | boolean) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)))
  const upstreamsOf = (channel: string) => library.filter((m) => m.channel_id === channel && (!model || m.kind === model.kind)).map((m) => m.upstream_model)
  const add = () => {
    const ch = channels[0]?.id ?? ''
    setRows((xs) => [
      ...xs,
      { channel_id: ch, upstream_model: upstreamsOf(ch)[0] ?? '', priority: '0', weight: '100', overrides: '', enabled: true },
    ])
  }

  const submit = async () => {
    if (!model) return
    setBusy(true)
    setError('')
    try {
      const routes: Route[] = rows.map((r, i) => ({
        channel_id: r.channel_id,
        upstream_model: r.upstream_model.trim(),
        priority: Number(r.priority),
        weight: Number(r.weight),
        enabled: r.enabled,
        request_overrides: parseJson(`#${i + 1} ${t(A.models.overrides)}`, r.overrides, {}, (name) => t(A.models.badJson, { name })),
      }))
      const out = await catalogApi.putRoutes(model.id, routes, reason.trim())
      toast.ok(t(A.models.savedVersion, { v: out.config_version }))
      onSaved()
    } catch (e) {
      setError(e instanceof Error && !('code' in e) ? e.message : errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const chOptions = channels.map((c) => ({ value: c.id, label: `${c.id}${c.enabled ? '' : ` (${t(A.status.disabled)})`}` }))
  return (
    <Modal open={model !== null} onClose={onClose} busy={busy} wide title={`${t(A.models.editRoutes)} · ${model?.id ?? ''}`}>
      <Form onSubmit={submit}>
        <p className="ap-muted" style={{ margin: 0 }}>
          {t(A.models.routesLead)}
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        {channels.length === 0 && <Notice tone="warning">{t(A.models.noChannels)}</Notice>}
        {rows.length === 0 && <Notice tone="warning">{t(A.models.noRoutes)}</Notice>}
        {rows.map((r, i) => {
          const ups = upstreamsOf(r.channel_id)
          const registered = ups.includes(r.upstream_model)
          const upOptions = registered || !r.upstream_model ? ups : [r.upstream_model, ...ups]
          return (
            <Card
              key={i}
              title={`#${i + 1}`}
              actions={
                <>
                  <Switch checked={r.enabled} onChange={(v) => patch(i, 'enabled', v)} label={t(A.models.enabled)} />
                  <Button size="sm" variant="ghost" icon={<IconTrash size={14} />} onClick={() => setRows((xs) => xs.filter((_, j) => j !== i))}>
                    {t(A.models.removeRoute)}
                  </Button>
                </>
              }
            >
              <div className="ap-stack" style={{ gap: 12 }}>
                <div className="ap-grid2">
                  <Field label={t(A.models.channel)}>
                    <Select
                      required
                      value={r.channel_id}
                      onChange={(e) => {
                        patch(i, 'channel_id', e.target.value)
                        patch(i, 'upstream_model', upstreamsOf(e.target.value)[0] ?? '')
                      }}
                      options={chOptions.some((o) => o.value === r.channel_id) ? chOptions : [{ value: r.channel_id, label: r.channel_id }, ...chOptions]}
                    />
                  </Field>
                  <Field label={t(A.models.upstreamModel)} hint={registered || !r.upstream_model ? undefined : t(A.models.unregistered)}>
                    <Select
                      required
                      value={r.upstream_model}
                      onChange={(e) => patch(i, 'upstream_model', e.target.value)}
                      options={upOptions.map((u) => ({ value: u, label: u }))}
                    />
                  </Field>
                  <Field label={t(A.models.priority)}>
                    <Input type="number" min={0} max={100} required value={r.priority} onChange={(e) => patch(i, 'priority', e.target.value)} />
                  </Field>
                  <Field label={t(A.models.weight)}>
                    <Input type="number" min={1} max={10000} required value={r.weight} onChange={(e) => patch(i, 'weight', e.target.value)} />
                  </Field>
                </div>
                <Field label={t(A.models.overrides)} hint={t(A.models.overridesHint)}>
                  <Textarea rows={3} spellCheck={false} className="ap-mono" value={r.overrides} onChange={(e) => patch(i, 'overrides', e.target.value)} />
                </Field>
              </div>
            </Card>
          )
        })}
        <div>
          <Button icon={<IconPlus size={16} />} disabled={channels.length === 0 || rows.length >= 50} onClick={add}>
            {t(A.models.addRoute)}
          </Button>
        </div>
        <Field label={`${t(C.reason)} (${t(C.optional)})`} hint={t(C.reasonHint)}>
          <Input maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

